// VehicleTrendHighChart.tsx
import { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    IconButton,
    Stack,
    Button,
    Autocomplete,
    TextField,
    InputAdornment,
} from '@mui/material';
import COEResultsCard from './COEResultsCard';
import HomepageTopAd from './HomePageTopAd';
import axiosInstance from '../services/axiosInstance';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';

type AdSpaceItem = {
    adSpaceId: number;
    dealerId: number;
    dealerName: string;
    name: string;
    startDate: string;
    endDate: string;
    adSpaceThumbnailUrl: string;
    adSpacePositionId: number;
    adSpacePositionName: string;
    adPositionCode: string;
    width: number;
    height: number;
    adSpaceImageId: number;
    fileName: string;
    adSpaceImageUrl: string;
};

type AdSpaceBannerProps = {
    adSpaceList: AdSpaceItem[];
};

const HeroSection = () => {

    const [adSpaceList, setAdSpaceList] = useState<any[]>([]);
    const navigate = useNavigate();
    const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
    const [vehicleClassData, setVehicleClassData] = useState<any[]>([]);
    const [vehicleBrandData, setVehicleBrandData] = useState<any[]>([]);
    const [vehicleModelData, setVehicleModelData] = useState<any[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedVehicleClassId, setSelectedVehicleClassId] = useState<number | null>(null);

    const combinedOptions = [
        ...vehicleBrandData.map((b) => ({ type: 'Brand', label: b.description })),
        ...vehicleModelData.map((m) => ({ type: 'Model', label: m.description })),
    ];


    useEffect(() => {

        const fetchAdData = async () => {
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    { adSpaceData: true },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: { adSpaceData?: any[] };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setAdSpaceList(result.responseData.adSpaceData || []);
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load ad data", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching ad data", { variant: "error" });
            }
        };

        fetchAdData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    {
                        vehicleBodyTypeData: true,
                        vehicleTransmissionTypeData: true,
                        vehicleClassData: true,
                        vehicleBrandData: true,
                        vehicleModelData: true,
                        dealerCompanyData: true,
                        adSpaceData: true,
                    },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        vehicleBodyTypeData?: any[];
                        vehicleTransmissionTypeData?: any[];
                        vehicleClassData?: any[];
                        vehicleBrandData?: any[];
                        vehicleModelData?: any[];
                        dealerCompanyData?: any[];
                        adSpaceData?: any[];
                    };
                };

                if (result.responseCode === "00" && result.responseData) {
                    setvehicleBodyTypeData(result.responseData.vehicleBodyTypeData || []);
                    setVehicleClassData(result.responseData.vehicleClassData || []);
                    setVehicleBrandData(result.responseData.vehicleBrandData || []);
                    setVehicleModelData(result.responseData.vehicleModelData || []);
                    setAdSpaceList(result.responseData.adSpaceData || []);

                    // 🔹 filter only homepage banner(s)
                    const homePageBanner = (result.responseData.adSpaceData || []).filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                } else {
                    enqueueSnackbar(result.responseMessage || "⚠️ Failed to load data", {
                        variant: "error",
                    });
                }
            } catch {
                enqueueSnackbar("❌ Error fetching vehicle metadata", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // 1. Updated FilterChip Component
    const FilterChip = ({ id, description, filterType, isSelected }: any) => {
        // Determine the background color based on selection status
        const backgroundColor = isSelected ? '#fff3cd' : '#f8f8f8';
        const textColor = isSelected ? '#bc8600' : '#000000';

        return (
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={0.3}
                onClick={() => {
                    if (id !== undefined) {
                        navigate(`/vehicle-listing?${filterType}=${encodeURIComponent(id.toString())}`);
                    }
                }}
                sx={{
                    cursor: 'pointer',
                    height: 40,
                    minWidth: 90,

                    // 👇 UPDATED: Increased padding for a nicer, spacious look
                    padding: '0 24px',


                    backgroundColor: backgroundColor,
                    borderRadius: '8px',
                    border: '1px solid ' + (isSelected ? '#ffc107' : 'transparent'),
                    boxShadow: 'none',
                    transition: 'all 0.2s ease-in-out', // Smooth transition for hover effects
                    '&:hover': {
                        backgroundColor: '#Ffd67b',
                        color: '#bc8600',
                    },
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="body2"
                    fontSize={14}
                    textAlign="center"
                    sx={{
                        lineHeight: 1.1,
                        fontWeight: 'normal',
                        whiteSpace: 'nowrap', // Optional: Keeps text on one line for a cleaner chip look
                        '&:hover': {
                            color: '#bc8600',
                        },
                    }}
                >
                    {description}
                </Typography>
            </Stack>
        );
    };

    const visibleClasses = vehicleClassData.filter(
        (cls: any) => cls.description !== "Private"
    );

    const filteredBodyTypes = bodyTypesData.filter(
        (type) => selectedVehicleClassId === null || type.vehicleClassId === selectedVehicleClassId
    );

    // 1. Create a reusable search function to avoid code duplication
    // We allow query to be a string, an object with a label, or null
    const handleSearch = (query: string | { label: string } | null) => {
        const term = typeof query === 'string' ? query : query?.label || searchText;

        if (term && term.trim().length > 0) {
            const encodedQuery = encodeURIComponent(term.trim());
            navigate(`/vehicle-listing?searchKeyword=${encodedQuery}`);
        }
    };


    // Assuming this block is inside the render/return of your parent component

    return (
        // FIX 1: Corrected the outer Grid container syntax
        <Grid container spacing={3} sx={{ py: 4, px: 2, justifyContent: 'center' }}>


            {/* ---------------- SECTION 1: VEHICLE CLASS BUTTONS ---------------- */}
            <Grid
                size={{ xs: 12, sm: 8, md: 11 }}
                sx={{ mx: 'auto', mb: 2 }}
            >
                <Grid
                    container
                    // 🚀 UPDATED: Tighter spacing on mobile
                    spacing={{ xs: 0.5, md: 1 }}
                    sx={{
                        width: '100%',
                        // 🚀 UPDATED: Forces buttons to stay on one line
                        flexWrap: 'nowrap'
                    }}
                >
                    {visibleClasses.map((cls) => {
                        const isSelected = selectedVehicleClassId === cls.vehicleClassId;
                        return (
                            // 🚀 UPDATED: size="grow" makes them share space equally
                            <Grid size="grow" key={cls.vehicleClassId}>
                                <Button
                                    fullWidth
                                    onClick={() => setSelectedVehicleClassId(cls.vehicleClassId)}
                                    sx={{
                                        // 🚀 UPDATED: Responsive font size
                                        fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                        height: { xs: '36px', md: '44px' },
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        border: '1px solid #ccc',
                                        color: isSelected ? '#fff' : '#000',
                                        backgroundColor: '#FFA100',
                                        boxShadow: isSelected ? '0px 4px 8px rgba(255, 161, 0, 0.4)' : 'none',
                                        transition: 'all 0.3s ease',

                                        // 🚀 UPDATED: Button Layout logic for tight spaces
                                        whiteSpace: { xs: 'normal', md: 'nowrap' }, // Allows text to wrap inside button on very small phones
                                        lineHeight: 1.2, // Tighter lines if text wraps
                                        minWidth: 'auto', // Allows button to shrink below default MUI width
                                        px: { xs: 0.5, md: 2 }, // Very small horizontal padding on mobile

                                        '&:hover': {
                                            backgroundColor: '#FFB733',
                                            color: '#fff',
                                            boxShadow: '0px 6px 12px rgba(255, 177, 51, 0.4)',
                                        },
                                    }}
                                >
                                    {cls.description.toUpperCase()} VEHICLES
                                </Button>
                            </Grid>
                        );
                    })}

                    {/* All Class Button */}
                    {/* 🚀 UPDATED: size="grow" makes this button share space equally with the others */}
                    <Grid size="grow">
                        <Button
                            fullWidth
                            onClick={() => setSelectedVehicleClassId(null)}
                            sx={{
                                // 🚀 UPDATED: Responsive font size
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                                height: { xs: '36px', md: '44px' },
                                fontWeight: 700,
                                borderRadius: 2,
                                border: selectedVehicleClassId === null ? '1px solid #FFA100' : '1px solid #ccc',
                                color: selectedVehicleClassId === null ? '#fff' : '#000',
                                backgroundColor: '#FFA100',
                                boxShadow: selectedVehicleClassId === null ? '0px 4px 8px rgba(255, 161, 0, 0.4)' : 'none',
                                transition: 'all 0.3s ease',

                                // 🚀 UPDATED: Button Layout logic for tight spaces
                                whiteSpace: { xs: 'normal', md: 'nowrap' },
                                lineHeight: 1.2,
                                minWidth: 'auto',
                                px: { xs: 0.5, md: 2 },

                                '&:hover': {
                                    backgroundColor: '#FFB733',
                                    color: '#fff',
                                    boxShadow: '0px 6px 12px rgba(255, 177, 51, 0.4)',
                                },
                            }}
                        >
                            {/* Optional: You might want to shorten this text on mobile using a conditional, e.g., isMobile ? "ALL TYPES" : ... */}
                            ALL COMMERCIAL VEHICLE TYPES
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            {/* ---------------- SECTION 2: SEARCH BAR ---------------- */}
            <Grid
                size={{ xs: 12, sm: 8, md: 11 }}
                sx={{
                    margin: '0 auto',
                    display: 'flex',
                    // 🚀 RESPONSIVE LAYOUT: Column on mobile, Row on desktop
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: { xs: 1, md: 0 } // Add gap when stacked vertically
                }}
            >
                {/* 1. Autocomplete Input */}
                <Box sx={{ flexGrow: 1 }}>
                    <Autocomplete
                        fullWidth
                        size="medium" // 'small' might be too small for touch targets, keeping medium but controlling height via CSS if needed
                        freeSolo
                        options={searchText.length > 0 ? combinedOptions : []}
                        getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.label
                        }
                        filterOptions={(options, { inputValue }) =>
                            options.filter((option) =>
                                option.label.toLowerCase().includes(inputValue.toLowerCase())
                            )
                        }
                        openOnFocus={false}
                        onChange={(event, value) => handleSearch(value)}
                        inputValue={searchText}
                        onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                        renderOption={(props, option) => (
                            <li {...props}>
                                {option.label}
                                <Typography variant="caption" sx={{ ml: 1, color: 'gray' }}>
                                    ({option.type})
                                </Typography>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search Vehicles..." // Shortened placeholder for mobile
                                variant="outlined"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch(searchText);
                                }}
                                sx={{
                                    '.MuiOutlinedInput-root': {
                                        borderRadius: '50px',
                                        backgroundColor: '#fff',
                                        boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
                                        fontSize: { xs: '0.9rem', md: '1rem' }, // Slightly smaller text
                                        '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.15)' },
                                        '&.Mui-focused': { boxShadow: '0px 0px 0px 2px #FFA100' },
                                    },
                                }}
                                InputProps={{ ...params.InputProps }}
                            />
                        )}
                    />
                </Box>

                {/* 2. Separate Search Button */}
                <Button
                    variant="contained"
                    onClick={() => handleSearch(searchText)}
                    sx={{
                        backgroundColor: '#FFA100',
                        color: '#fff',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '50px',
                        // 🚀 RESPONSIVE BUTTON: Full width on mobile, fixed height
                        width: { xs: '100%', md: 'auto' },
                        height: { xs: '45px', md: '56px' },
                        ml: { xs: 0, md: 2 }, // Margin left only on desktop
                        px: 4,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#FFB300',
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                        },
                    }}
                >
                    Search
                </Button>
            </Grid>

            {/* ---------------- SECTION 3: EXPLORE VEHICLE TYPES (CHIPS) ---------------- */}
            <Grid
                size={{ xs: 12, sm: 8, md: 11 }}
                sx={{
                    margin: '0 auto',
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'center'
                }}
            >
                {!loading && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            // 🚀 RESPONSIVE LAYOUT: Stack header on top of chips on mobile
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: { xs: 1, md: 7 }, // Reduced gap on mobile
                            overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ flexShrink: 0, pl: { xs: 1, md: 0 } }}>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} // Smaller header on mobile
                            >
                                Explore Vehicle Types
                            </Typography>
                        </Box>

                        {/* Chips Container */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                width: { xs: '100%', md: 'auto' }, // Full width on mobile for scrolling
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 2, md: 7 }, // Tighter gap between chips on mobile
                                flexWrap: 'nowrap',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                pb: 1,
                                '&::-webkit-scrollbar': { height: '4px' }, // Thinner scrollbar
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '10px' },
                                px: 0.5
                            }}
                        >
                            {(showAll ? filteredBodyTypes : filteredBodyTypes.slice(0, 5)).map(
                                (type, index) => {
                                    const currentFilterValue = new URLSearchParams(window.location.search).get('vehicleBodyTypeIds');
                                    const isSelected = currentFilterValue === type.vehicleBodyTypeId.toString();

                                    return (
                                        <Box key={index} sx={{ flexShrink: 0 }}>
                                            <FilterChip
                                                id={type.vehicleBodyTypeId}
                                                description={type.description}
                                                filterType="vehicleBodyTypeIds"
                                                isSelected={isSelected}
                                            // Assuming FilterChip accepts sx or style, or passes props down. 
                                            // If not, you might need to wrap it or modify the component itself for smaller mobile size.
                                            />
                                        </Box>
                                    );
                                }
                            )}
                        </Box>

                        {/* View More Button */}
                        {filteredBodyTypes.length > 5 && (
                            <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', md: 'center' } }}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setShowAll((prev) => !prev)}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap',
                                        fontSize: { xs: '0.8rem', md: '0.9rem' }
                                    }}
                                >
                                    {showAll ? 'View Less' : 'View More'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                {/* COE Box - Left */}
                <COEResultsCard />
            </Grid>
            {/* Ad Section (Right Side) */}
            <Grid
                size={{ xs: 12, md: 5 }}
                sx={{ mt: { xs: 4, md: 0 } }} // Space on top for mobile
            >
                {/* 👇 SCROLL WRAPPER */}
                <Box sx={{
                    width: '100%',
                    overflowX: 'auto',
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'center' },

                    // Hide scrollbar aesthetics
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',

                    // ✅ PADDING CHANGE: 
                    // 0 on mobile (removes white gaps/background on sides)
                    // 2 (16px) on tablet/desktop
                    px: { xs: 0, sm: 2 },

                    // Optional: Ensures the box itself is transparent
                    bgcolor: 'transparent'
                }}>
                    <HomepageTopAd adSpaceList={adSpaceList} />
                </Box>
            </Grid>




        </Grid>
    );

};

export default HeroSection;
